import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  CssBaseline,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { bookmakerLogos } from '../assets/bookmakerLogos';
import GameLogModal from '../components/GameLogModal';

// Static list of bookmakers in specified order
const staticBookmakers = [
  'DraftKings',
  'Pinnacle',
  'Bookmaker',
  'FanDuel',
  //'theScore',
  'Caesars',
  'BetMGM',
  'BetRivers',  
  'Builder',
  'Bet365',
  'Bovada',
  'Fliff',
  'Hard Rock',
  'Fanatics',
  'Prophet Exchange',
  'NoVig',
    'Polymarket',
  'Kalshi',
  'Bet105',
];

// Bookmaker links configuration
const bookmakerLinks = {
  'Prophet Exchange': 'https://prophetx.onelink.me/E5Yi/kxa7a4q7',
  'NoVig': 'https://novig.onelink.me/JHQQ/1vgeffy0',
  'DraftKings': 'https://dksb.sng.link/As9kz/85o9?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D356902394%26wpcid%3D239969%26wpcn%3DBet5Win150%26wpsrc%3DBetsperts%26wpcrid%3D%26wpscn%3D&pcid=239969&psn=Betsperts&pcn=Bet5Win150_4For4',
  'FanDuel': 'https://wlfanduelus.adsrv.eacdn.com/C.ashx?btag=a_21545b_16c_&affid=11060&siteid=21545&adid=16&c=',
  'Bet365': 'https://www.bet365.com/olp/open-account?affiliate=365_02038136',
  'BetMGM': 'https://mediaserver.betmgmpartners.com/renderBanner.do?zoneId=1700764',
  'Caesars': 'https://wlwilliamhillus.adsrv.eacdn.com/C.ashx?btag=a_12260b_2091c_&affid=47&siteid=12260&adid=2091&c=',
  'Fanatics': 'https://go.affiliationcloud.com/0002JY',
};

// Static period types per sport
const periodTypesBySport = {
  NBA: [
    'Full Game',
    'First Half',
    'Second Half',
    'First Quarter',
    'Second Quarter',
    'Third Quarter',
    'Fourth Quarter',
  ],
    CBB: [
    'Full Game',
    'First Half',
    'Second Half',
    'First Quarter',
    'Second Quarter',
    'Third Quarter',
    'Fourth Quarter',
  ],
  WNBA: [
    'Full Game',
    'First Half',
    'Second Half',
    'First Quarter',
    'Second Quarter',
    'Third Quarter',
    'Fourth Quarter',
  ],
  MLB: ['Full Game'],
  NHL: ['Full Game', 'First Period', 'Second Period', 'Third Period'],
  NFL: ['Full Game', 'First Half', 'Second Half','First Quarter','Second Quarter','Third Quarter','Fourth Quarter'],
  CFB: ['Full Game', 'First Half', 'Second Half','First Quarter','Second Quarter','Third Quarter','Fourth Quarter'],
    PGA: ['Full Game'],
};

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#121212',
        },
        indicator: {
          backgroundColor: '#ff1744',
          height: '2px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#121212',
          borderRadius: '8px 8px 0 0',
          transition: 'background-color 0.2s, color 0.2s',
          '&.Mui-selected': {
            color: '#ffffff',
            backgroundColor: '#121212',
          },
          '&:hover': {
            backgroundColor: '#1a1a1a',
          },
        },
      },
    },
  },
});

const Props = ({ bets }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiRef = useRef(null);
  
const [sport, setSport] = useState(() => {
  const saved = localStorage.getItem('propsSport');
  return saved || 'NFL';
});
const [betType, setBetType] = useState(() => {
  const saved = localStorage.getItem('propsBetType');
  return saved || 'Passing Yards';
});
const [periodType, setPeriodType] = useState(() => {
  const saved = localStorage.getItem('propsPeriodType');
  return saved || 'Full Game';
});
const [pageSize, setPageSize] = useState(10);
const [page, setPage] = useState(0);
const [selectedMatchup, setSelectedMatchup] = useState(() => {
  const saved = localStorage.getItem('propsSelectedMatchup');
  return saved || 'All';
});
  
  // Game Log Modal State
  const [gameLogModalOpen, setGameLogModalOpen] = useState(false);
  const [selectedPlayerData, setSelectedPlayerData] = useState(null);
  const [gameLogData, setGameLogData] = useState(null);
  const [gameLogLoading, setGameLogLoading] = useState(false);
  const [gameLogSport, setGameLogSport] = useState('NFL');
  const gameLogSportRef = useRef('NFL'); // Use ref to avoid state timing issues
  
  // Add state for current time to trigger re-renders for color updates
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Update current time every second for smooth color transitions
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Add state for selected books
  const [selectedBooks, setSelectedBooks] = useState(() => {
    const saved = localStorage.getItem(`propsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading selected books:', e);
      }
    }
    return staticBookmakers.filter(book => book !== 'Prophet Exchange' && book !== 'NoVig');
  });

  // Save selected books when they change
  useEffect(() => {
    localStorage.setItem(`propsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`, JSON.stringify(selectedBooks));
  }, [selectedBooks, isMobile]);

  useEffect(() => {
  localStorage.setItem('propsSport', sport);
}, [sport]);

useEffect(() => {
  localStorage.setItem('propsBetType', betType);
}, [betType]);

useEffect(() => {
  localStorage.setItem('propsPeriodType', periodType);
}, [periodType]);

useEffect(() => {
  localStorage.setItem('propsSelectedMatchup', selectedMatchup);
}, [selectedMatchup]);
  // Use static period types based on the selected sport
  const periodTypes = useMemo(() => {
    return periodTypesBySport[sport] || ['Full Game'];
  }, [sport]);

  // Reset periodType when sport changes if current periodType is not available
  useEffect(() => {
    if (!periodTypes.includes(periodType)) {
      setPeriodType(periodTypes[0] || 'Full Game');
    }
  }, [periodTypes, periodType]);

  // Reset selectedMatchup to 'All' when sport changes
  useEffect(() => {
    setSelectedMatchup('All');
  }, [sport]);

  // Handle player name click to fetch game log
  const handlePlayerClick = async (rowData) => {
    // Only fetch game logs for NFL and NBA
    if (sport !== 'NFL' && sport !== 'NBA') return;
    
    const personId = rowData.PersonId;
    
    // Don't proceed if no PersonId
    if (!personId) {
      console.log('No PersonId for this player');
      return;
    }

    // Determine league for API endpoint
    const league = sport === 'NBA' ? 'nba' : 'nfl';
    console.log('Props handlePlayerClick - sport:', sport, 'league:', league);
    gameLogSportRef.current = sport; // Store in ref immediately

    setGameLogLoading(true);
    
    try {
      const response = await fetch(`https://ws.betpros.ai:8080/api/player-game-logs/${league}/${personId}`);
      
      if (!response.ok) {
        console.error('Failed to fetch game log:', response.status);
        setGameLogLoading(false);
        return;
      }
      
      const data = await response.json();
      
      // Only open modal if we have game log data
      if (data && data.gameLogs && data.gameLogs.length > 0) {
        setSelectedPlayerData({
          PlayerName: data.playerName,
          Position: data.position,
          Team: data.team,
          teamLogo: rowData.teamLogo,
          photoUrl: data.photoUrl,
          Height: data.Height,
          Weight: data.Weight,
          Age: data.Age,
          College: data.College,
          Experience: data.Experience,
          EventStart: rowData.eventStart,
          AwayShort: rowData.awayShort,
          HomeShort: rowData.homeShort,
          AwayLogo: rowData.awayLogo,
          HomeLogo: rowData.homeLogo
        });
        setGameLogData(data.gameLogs);
        setGameLogModalOpen(true);
      } else {
        console.log('No game log data available for this player');
      }
    } catch (error) {
      console.error('Error fetching game log:', error);
    } finally {
      setGameLogLoading(false);
    }
  };

  // Close modal handler
  const handleCloseModal = () => {
    setGameLogModalOpen(false);
    setSelectedPlayerData(null);
    setGameLogData(null);
  };

  // Function to calculate opacity based on time difference (0-5 minutes scale)
  const calculateOpacity = (oddsChangedDate) => {
    if (!oddsChangedDate) return 0;
    const changedTime = new Date(oddsChangedDate).getTime();
    const diffMs = currentTime - changedTime;
    const diffSeconds = diffMs / 1000;
    
    if (diffSeconds < 0) return 1;
    if (diffSeconds > 300) return 0; // 5 minutes
    return 1 - (diffSeconds / 300); // Dividing by 300 seconds
  };

  // Dynamically generate bet types and matchups from available data
// Dynamically generate bet types and matchups from available data
const { betTypes, matchups } = useMemo(() => {
  if (!Array.isArray(bets)) {
    console.log('Bets data is not an array:', bets);
    return { betTypes: [], matchups: [] };
  }
  const types = new Set();
  const matchupData = new Map();
  const isPGA = sport === 'PGA';

  bets.forEach(bet => {
    if (bet && bet.betCategory === 'Prop' && bet.leagueName === sport) {
      if (bet.periodTypeName === periodType) {
        if (bet.betTypeName) types.add(bet.betTypeName);
        
        // For PGA, use eventName; for other sports, use eventShortName
        const matchupKey = isPGA ? bet.eventName : bet.eventShortName;
        
        if (matchupKey && bet.eventStart) {
          const currentData = matchupData.get(matchupKey);
          const betTime = new Date(bet.eventStart).getTime();
          if (!currentData || betTime < currentData.eventStart) {
            matchupData.set(matchupKey, {
              eventStart: betTime,
              awayLogo: bet.awayLogo || '',
              homeLogo: bet.homeLogo || '',
              eventName: bet.eventName || '',
            });
          }
        }
      }
    }
  });
  const sortedTypes = Array.from(types).sort();
  const sortedMatchups = Array.from(matchupData.entries())
    .sort((a, b) => {
      const timeA = a[1].eventStart && !isNaN(a[1].eventStart) ? a[1].eventStart : Number.MAX_SAFE_INTEGER;
      const timeB = b[1].eventStart && !isNaN(b[1].eventStart) ? b[1].eventStart : Number.MAX_SAFE_INTEGER;
      return timeA - timeB;
    })
    .map(entry => {
      // Parse team short names from eventShortName (format: "AWAY @ HOME")
      const parts = entry[0].split('@').map(s => s.trim());
      return {
        eventShortName: entry[0],
        awayLogo: entry[1].awayLogo,
        homeLogo: entry[1].homeLogo,
        awayShort: parts[0] || '',
        homeShort: parts[1] || '',
        eventName: entry[1].eventName,
        isPGA: isPGA,
      };
    });
  return { betTypes: sortedTypes, matchups: sortedMatchups };
}, [bets, sport, periodType]);

  // Set initial betType when betTypes change
  useEffect(() => {
    if (betTypes.length > 0 && (!betType || !betTypes.includes(betType))) {
      if (sport === 'NFL' && betTypes.includes('Passing Yards')) {
        setBetType('Passing Yards');
      } else if (sport === 'MLB' && betTypes.includes('Pitcher Strikeouts')) {
        setBetType('Pitcher Strikeouts');
      } else if (sport === 'CFB' && betTypes.includes('Passing Yards')) {
        setBetType('Passing Yards');
      } else {
        setBetType(betTypes[0]);
      }
    } else if (betTypes.length === 0) {
      setBetType('');
    }
  }, [betTypes, betType, sport]);

  // Handle books filter change
  const handleBooksFilterChange = (event) => {
    const value = event.target.value;
    if (value[value.length - 1] === 'all') {
      setSelectedBooks(selectedBooks.length === staticBookmakers.length ? [] : staticBookmakers);
    } else {
      setSelectedBooks(value);
    }
  };

  // Render value for books dropdown
  const renderBooksValue = (selected) => {
    if (!selected.length) return 'None';
    if (selected.length === staticBookmakers.length) return 'All';
    if (selected.length > 1) return `Multiple (${selected.length})`;
    return selected[0];
  };

  // Group bets into a single table
  const tableData = useMemo(() => {
    if (!Array.isArray(bets)) {
      console.log('Bets data is not an array:', bets);
      return { rows: [], columns: [] };
    }

    const filteredBets = bets.filter(bet => {
      if (!bet) return false;
      return (
        bet.betCategory === 'Prop' &&
        bet.leagueName === sport &&
        bet.betTypeName === betType &&
        bet.periodTypeName === periodType
      );
    });

    console.log('Filtered prop bets for PropExplorer table:', filteredBets.length, 'items', filteredBets);

    const eventPlayerGroups = {};
    filteredBets.forEach(bet => {
      const eventKey = `${bet.eventName}-${bet.playerName || ''}`;
      if (!eventPlayerGroups[eventKey]) {
        eventPlayerGroups[eventKey] = {
          event: bet.eventName,
          player: bet.playerName || '',
          position: bet.position || '',
          eventShortName: bet.eventShortName || '',
          startTime: bet.eventStart,
          awayLogo: bet.awayLogo || '',
          homeLogo: bet.homeLogo || '',
          bets: [],
        };
      }
      eventPlayerGroups[eventKey].bets.push(bet);
    });

    const rowsWithStartTime = [];
    let rowId = 0;

    Object.entries(eventPlayerGroups).forEach(([eventKey, group]) => {
      const row = {
        id: rowId++,
        eventName: group.event,
        playerName: group.player,
        position: group.position,
        displayName: group.position ? `${group.player}` : group.player,
        eventShortName: group.eventShortName,
        awayLogo: group.awayLogo,
        homeLogo: group.homeLogo,
        eventStart: group.startTime,
        // Add these fields for game log modal
        PersonId: group.bets[0]?.PersonId || group.bets[0]?.personId,
        teamLogo: group.bets[0]?.teamLogo,
        awayShort: group.bets[0]?.awayShort,
        homeShort: group.bets[0]?.homeShort,
        eventStartFormatted: {
          date: group.startTime
            ? new Date(group.startTime).toLocaleDateString('en-US', {
                year: '2-digit',
                month: 'numeric',
                day: 'numeric',
              })
            : '',
          time: group.startTime
            ? new Date(group.startTime).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            : '',
        },
        bookmakerOdds: {},
        bestOddsBookmakers: { Over: '', Under: '' },
      };

      // Filter by selected matchup
      if (selectedMatchup !== 'All') {
        // For PGA, match against eventName; for other sports, match against eventShortName
        const matchField = sport === 'PGA' ? row.eventName : row.eventShortName;
        if (matchField !== selectedMatchup) {
          return;
        }
      }

      const oddsBets = group.bets.filter(bet => bet.betTypeName === betType && bet.periodTypeName === periodType);

      const outcomes = { Over: {}, Under: {} };
      const outcomeDetails = { Over: {}, Under: {} };
      const outcomeUpdatedDates = { Over: {}, Under: {} };
      
      oddsBets.forEach(bet => {
        const outcome = bet.side === 'Over' ? 'Over' : 'Under';
        outcomes[outcome][bet.marketSourceName] = bet.price;
        outcomeDetails[outcome][`${bet.marketSourceName}Points`] = bet.points;
        outcomeDetails[outcome][`${bet.marketSourceName}Logo`] = bookmakerLogos[bet.marketSourceName] || '';
        outcomeDetails[outcome][`${bet.marketSourceName}OutcomeName`] = bet.side || outcome;
        // FIX: Only add o/u prefix if there are actual points (not for Winner bets)
        const hasPoints = bet.points !== null && bet.points !== undefined && bet.points !== 0 && bet.points !== '0';
        outcomeDetails[outcome][`${bet.marketSourceName}LineDisplay`] = hasPoints ? `${outcome === 'Over' ? 'o' : 'u'}${bet.points}` : '';
        outcomeUpdatedDates[outcome][bet.marketSourceName] = bet.oddsChangedDate;
      });

      // Calculate best odds only from selected books
      const bestOdds = {};
      const bestOddsKeys = ['Over', 'Under'];
      bestOddsKeys.forEach(outcome => {
        const entries = [];
        selectedBooks.forEach(book => {
          const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
          const points = outcomeDetails[outcome][`${book}Points`];
          if (price !== null) {
            // FIX: Handle empty strings and invalid values when parsing points
            const parsedPoints = (points !== undefined && points !== null && points !== '') ? parseFloat(points) : null;
            entries.push({
              book,
              price,
              points: (parsedPoints !== null && !Number.isNaN(parsedPoints)) ? parsedPoints : null,
              logo: outcomeDetails[outcome][`${book}Logo`],
              outcomeName: outcomeDetails[outcome][`${book}OutcomeName`],
              lineDisplay: outcomeDetails[outcome][`${book}LineDisplay`],
            });
          }
        });

        let bestEntry = null;
        if (entries.length > 0) {
          // FIX: Handle null, undefined, and NaN points - treat all as 0 for comparison
          entries.sort((a, b) => {
            const pointsA = (a.points === null || a.points === undefined || Number.isNaN(a.points)) ? 0 : a.points;
            const pointsB = (b.points === null || b.points === undefined || Number.isNaN(b.points)) ? 0 : b.points;
            if (pointsA !== pointsB) {
              return outcome === 'Over' ? pointsA - pointsB : pointsB - pointsA;
            }
            // Higher price is better
            return b.price - a.price;
          });
          bestEntry = entries[0];
        }

        if (bestEntry) {
          bestOdds[outcome] = {
            price: bestEntry.price,
            points: bestEntry.points,
            logo: bestEntry.logo,
            outcomeName: bestEntry.outcomeName,
            lineDisplay: bestEntry.lineDisplay,
          };
          row.bestOddsBookmakers[outcome] = bestEntry.book;
        } else {
          bestOdds[outcome] = {
            price: null,
            points: null,
            logo: '',
            outcomeName: outcome,
            lineDisplay: '',
          };
          row.bestOddsBookmakers[outcome] = '';
        }
      });

      staticBookmakers.forEach(book => {
        const odds = {
          updatedDates: {}
        };
        bestOddsKeys.forEach(outcome => {
          const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
          if (price !== null) {
            const formattedPrice = price > 0 ? `+${price}` : `${price}`;
            const line = outcomeDetails[outcome][`${book}LineDisplay`] || '';
            odds[outcome] = line ? `${line} ${formattedPrice}` : formattedPrice;
            odds.updatedDates[outcome] = outcomeUpdatedDates[outcome][book];
          } else {
            odds[outcome] = '';
            odds.updatedDates[outcome] = null;
          }
        });
        row.bookmakerOdds[book] = odds;
        row[book] = '';
      });

      bestOddsKeys.forEach(outcome => {
        const { price, lineDisplay } = bestOdds[outcome] || {};
        if (price !== null) {
          const formattedPrice = price > 0 ? `+${price}` : `${price}`;
          bestOdds[outcome].display = lineDisplay ? `${lineDisplay} ${formattedPrice}` : formattedPrice;
        } else {
          bestOdds[outcome].display = '';
        }
      });

      row.bestOdds = bestOdds;
      rowsWithStartTime.push(row);
    });

    rowsWithStartTime.sort((a, b) => {
      const dateA = a.eventStart && !isNaN(new Date(a.eventStart).getTime()) ? new Date(a.eventStart) : new Date(9999, 0, 1);
      const dateB = b.eventStart && !isNaN(new Date(b.eventStart).getTime()) ? new Date(b.eventStart) : new Date(9999, 0, 1);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      const playerA = a.playerName || '';
      const playerB = b.playerName || '';
      return playerA.localeCompare(playerB);
    });

    rowsWithStartTime.forEach(row => {
      if (!row.eventStart || isNaN(new Date(row.eventStart).getTime())) {
        console.log('Invalid eventStart for row:', row);
      }
    });

    return { rows: rowsWithStartTime, columns: [] };
  }, [bets, sport, betType, periodType, selectedMatchup, selectedBooks]);

  // Get saved column order for current context
  const getSavedColumnOrder = () => {
    try {
      const key = `propsColumnOrder_${sport}_${betType}_${periodType}_${selectedMatchup}_${isMobile ? 'mobile' : 'desktop'}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading column order:', error);
    }
    return null;
  };

  // Save column order for current context
  const saveColumnOrder = (order) => {
    try {
      const key = `propsColumnOrder_${sport}_${betType}_${periodType}_${selectedMatchup}_${isMobile ? 'mobile' : 'desktop'}`;
      localStorage.setItem(key, JSON.stringify(order));
    } catch (error) {
      console.error('Error saving column order:', error);
    }
  };

  // Check if current bet type has no points (like Winner bets)
  const isNoPointsBetType = useMemo(() => {
    // Check if any of the current bets have meaningful points
    if (!Array.isArray(bets)) return false;
    const relevantBets = bets.filter(bet => 
      bet && 
      bet.betCategory === 'Prop' && 
      bet.leagueName === sport && 
      bet.betTypeName === betType
    );
    // If no bets have points > 0, it's a "no points" bet type like Winner
    return relevantBets.length > 0 && relevantBets.every(bet => 
      bet.points === null || bet.points === undefined || bet.points === 0 || bet.points === '0'
    );
  }, [bets, sport, betType]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'eventName',
        headerName: '',
        minWidth: isMobile ? 135 : 165,
        flex: isMobile ? 1 : 1,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => {
          const [awayTeam, homeTeam] = params.row.eventShortName ? params.row.eventShortName.split('@') : ['', ''];
          const isNFL = sport === 'NFL';
          const isNBA = sport === 'NBA';
          const isPGA = sport === 'PGA';
          const hasPersonId = params.row.PersonId;
          const isClickable = (isNFL || isNBA) && hasPersonId;
          
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '0', justifyContent: 'center', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: isNoPointsBetType ? '70px' : '35px', paddingLeft: isMobile ? '4px' : '8px', paddingRight: isMobile ? '0px' : '0' }}>
                <Typography
                  onClick={isClickable ? (e) => {
                    e.stopPropagation();
                    handlePlayerClick(params.row);
                  } : undefined}
                  sx={{
                    fontSize: isMobile ? '12px' : '14px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    cursor: isClickable ? 'pointer' : 'default',
                    '&:hover': isClickable ? {
                      textDecoration: 'underline'
                    } : {}
                  }}
                >
                  {params.row.displayName}
                </Typography>
              </Box>
              {!isNoPointsBetType && (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px', paddingRight: isMobile ? '0px' : '0' }}>
                  {isPGA ? (
                    // PGA: Show event name instead of team matchup
                    <Typography sx={{ fontSize: isMobile ? '11px' : '14px', color: '#aaa' }}>
                      {params.row.eventName}
                    </Typography>
                  ) : (
                    // Other sports: Show away @ home
                    <>
                      {params.row.awayLogo && (
                        <img
                          src={params.row.awayLogo}
                          alt="Away Team Logo"
                          style={{ width: isMobile ? '16px' : '24px', height: isMobile ? '16px' : '24px', objectFit: 'contain', marginRight: '2px' }}
                        />
                      )}
                      <Typography sx={{ fontSize: isMobile ? '11px' : '14px' }}>{awayTeam}</Typography>
                      <Typography sx={{ fontSize: isMobile ? '10px' : '12px', mx: '2px' }}>@</Typography>
                      {params.row.homeLogo && (
                        <img
                          src={params.row.homeLogo}
                          alt="Home Team Logo"
                          style={{ width: isMobile ? '16px' : '24px', height: isMobile ? '16px' : '24px', objectFit: 'contain', marginRight: '2px' }}
                        />
                      )}
                      <Typography sx={{ fontSize: isMobile ? '11px' : '14px' }}>{homeTeam}</Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        },
      },
      {
        field: 'eventStartFormatted',
        headerName: '',
        minWidth: isMobile ? 70 : 85,
        maxWidth: isMobile ? 70 : 85,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '0', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ height: isNoPointsBetType ? '35px' : '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.date}</Typography>
            </Box>
            {!isNoPointsBetType && (
              <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
                <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.time}</Typography>
              </Box>
            )}
            {isNoPointsBetType && (
              <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
                <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.time}</Typography>
              </Box>
            )}
          </Box>
        ),
      },
    {
      field: 'bestOdds',
      headerName: 'Best Odds',
      headerAlign: 'right',
      minWidth: isMobile ? 110 : 125,
      flex: 1,
      sortable: true,
      disableReorder: true,
      sortComparator: (v1, v2) => {
        // Sort by Over price (higher = better for bettor)
        const price1 = v1?.Over?.price ?? -Infinity;
        const price2 = v2?.Over?.price ?? -Infinity;
        return price1 - price2;
      },
      renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: isMobile ? '4px' : '8px' }}>
            <Typography sx={{ fontSize: isMobile ? '14px' : '16px', color: '#ffffff' }}>Best Odds</Typography>
          </Box>
        ),
        renderCell: (params) => {
          const value = params.value;
          // FIX: Only show outcomes that have data
          const outcomes = ['Over', 'Under'].filter(o => value[o]?.display);
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
              {outcomes.map((outcome, idx) => {
                const { display, logo, outcomeName } = value[outcome] || {};
                if (!display) return null;
                return (
                  <Box key={outcome} sx={{ display: 'flex', alignItems: 'center', height: isNoPointsBetType ? '70px' : '35px', paddingRight: '8px' }}>
                    <Typography
                      sx={{
                        fontSize: isMobile ? '12px' : '14px',
                        textAlign: 'right',
                        pr: isMobile ? '4px' : '8px',
                        flex: 1,
                      }}
                    >
                      {display}
                    </Typography>
                    {logo && (
                      <img
                        src={logo}
                        alt={`${outcomeName} Logo`}
                        style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
    ];

    const visibleBookmakers = selectedBooks;
    
    const bookmakerColumns = {};
    visibleBookmakers.forEach(book => {
      bookmakerColumns[book] = {
        field: book,
        headerName: '',
        headerAlign: 'right',
        minWidth: isMobile ? 90 : 100,
        flex: 1,
        sortable: false,
        disableReorder: false,
        renderHeader: () => {
          const hasLink = !!bookmakerLinks[book];
          
          return (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center',
                width: '100%', 
                height: '100%',
                paddingRight: isMobile ? '4px' : '12px',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                userSelect: 'none',
              }}
              onClick={(e) => {
                if (hasLink && e.detail > 0) {
                  const link = bookmakerLinks[book];
                  if (link) {
                    window.open(link, '_blank', 'noopener,noreferrer');
                  }
                }
              }}
            >
              {bookmakerLogos[book] ? (
                <img
                  src={bookmakerLogos[book]}
                  alt={book}
                  style={{ 
                    width: isMobile ? '50px' : '70px', 
                    height: 'auto', 
                    objectFit: 'contain',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                />
              ) : (
                <Box sx={{ width: isMobile ? '50px' : '70px', height: 'auto' }} />
              )}
            </Box>
          );
        },
        renderCell: (params) => {
          const row = params.row;
          const bookOdds = row.bookmakerOdds && row.bookmakerOdds[params.field];
          const bestBookmaker = row.bestOddsBookmakers;
          if (bookOdds) {
            // FIX: Only show outcomes that have data
            const outcomes = ['Over', 'Under'].filter(o => bookOdds[o]);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
                {outcomes.map((outcome, idx) => {
                  const opacity = calculateOpacity(bookOdds.updatedDates?.[outcome]);
                  
                  return (
                    <Box
                      key={outcome}
                      sx={{
                        height: isNoPointsBetType ? '70px' : '35px',
                        display: 'flex',
                        alignItems: 'center',
                        border: params.field === bestBookmaker[outcome] ? '2px solid #00cc00' : 'none',
                        borderRadius: '0',
                        width: '100%',
                        backgroundColor: `rgba(13, 71, 161, ${opacity})`,
                        transition: 'background-color 0.3s ease',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: isMobile ? '12px' : '14px',
                          textAlign: 'right',
                          pr: isMobile ? '4px' : '12px',
                          flex: 1,
                        }}
                      >
                        {bookOdds[outcome] || ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          }
          return <Box sx={{ textAlign: 'right', pr: '12px' }}><Typography sx={{ fontSize: '14px' }}></Typography></Box>;
        },
      };
    });

    const savedOrder = getSavedColumnOrder();
    const finalColumns = [...baseColumns];
    
    if (savedOrder && savedOrder.bookmakers) {
      savedOrder.bookmakers.forEach(book => {
        if (bookmakerColumns[book]) {
          finalColumns.push(bookmakerColumns[book]);
        }
      });
      
      visibleBookmakers.forEach(book => {
        if (!savedOrder.bookmakers.includes(book) && bookmakerColumns[book]) {
          finalColumns.push(bookmakerColumns[book]);
        }
      });
    } else {
      visibleBookmakers.forEach(book => {
        finalColumns.push(bookmakerColumns[book]);
      });
    }

    return finalColumns;
  }, [sport, betType, periodType, selectedMatchup, isMobile, selectedBooks, currentTime, isNoPointsBetType]);

  // Handle column reordering
  const handleColumnOrderChange = (params) => {
    if (apiRef.current) {
      const state = apiRef.current.state;
      const orderedFields = state.columns.orderedFields || state.columns.all;
      
      if (orderedFields) {
        const fixedColumns = ['eventName', 'eventStartFormatted', 'bestOdds'];
        const bookmakers = orderedFields.filter(field => !fixedColumns.includes(field));
        
        saveColumnOrder({
          fixed: fixedColumns,
          bookmakers: bookmakers
        });
      }
    }
  };

  // Restore column order when grid is ready
  useEffect(() => {
    if (apiRef.current && selectedBooks.length > 0) {
      const savedOrder = getSavedColumnOrder();
      if (savedOrder && savedOrder.bookmakers) {
        try {
          const fixedColumns = ['eventName', 'eventStartFormatted', 'bestOdds'];
          const newOrder = [...fixedColumns];
          
          savedOrder.bookmakers.forEach(book => {
            if (selectedBooks.includes(book)) {
              newOrder.push(book);
            }
          });
          
          selectedBooks.forEach(book => {
            if (!newOrder.includes(book)) {
              newOrder.push(book);
            }
          });

          apiRef.current.setColumnIndex && 
            newOrder.forEach((field, index) => {
              apiRef.current.setColumnIndex(field, index);
            });
        } catch (error) {
          console.error('Error restoring column order:', error);
        }
      }
    }
  }, [selectedBooks, sport, betType, periodType, selectedMatchup]);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
            width: '100vw',
            position: 'relative',
          }}
        >
          <Box
            className="filter-section"
            sx={{
              padding: isMobile ? '8px' : '16px',
              position: 'sticky',
              top: 0,
              zIndex: 3,
              backgroundColor: 'background.default',
            }}
          >
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={isMobile ? 1 : 2}
              alignItems={isMobile ? "stretch" : "center"}
            >
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="league-select-label" shrink sx={{ color: '#ffffff' }}>
                  League
                </InputLabel>
                <Select
                  labelId="league-select-label"
                  label="League"
                  notched
                  value={sport}
                  onChange={(e) => setSport(e.target.value)}
                >
                  <MenuItem value="NFL">NFL</MenuItem>
                  <MenuItem value="NBA">NBA</MenuItem>
                  <MenuItem value="MLB">MLB</MenuItem>
                  <MenuItem value="NHL">NHL</MenuItem>
                  <MenuItem value="CFB">CFB</MenuItem>
                  <MenuItem value="WNBA">WNBA</MenuItem>
                  <MenuItem value="CBB">CBB</MenuItem>
                  <MenuItem value="PGA">PGA</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="bet-type-select-label" shrink sx={{ color: '#ffffff' }}>
                  Bet Type
                </InputLabel>
                <Select
                  labelId="bet-type-select-label"
                  label="Bet Type"
                  notched
                  value={betType}
                  onChange={(e) => setBetType(e.target.value)}
                >
                  {betTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="period-type-select-label" shrink sx={{ color: '#ffffff' }}>
                  Period Type
                </InputLabel>
                <Select
                  labelId="period-type-select-label"
                  label="Period Type"
                  notched
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                >
                  {periodTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 140 }}>
                <InputLabel id="books-filter-label" shrink sx={{ color: '#ffffff' }}>
                  Books
                </InputLabel>
                <Select
                  multiple
                  labelId="books-filter-label"
                  label="Books"
                  notched
                  value={selectedBooks}
                  onChange={handleBooksFilterChange}
                  renderValue={renderBooksValue}
                >
                  <MenuItem value="all">
                    <Checkbox checked={selectedBooks.length === staticBookmakers.length} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {staticBookmakers.map((book) => (
                    <MenuItem key={book} value={book}>
                      <Checkbox checked={selectedBooks.includes(book)} />
                      <ListItemText primary={book} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Box sx={{ backgroundColor: 'background.default', marginTop: isMobile ? '8px' : '0' }}>
              <Tabs
                value={selectedMatchup}
                onChange={(e, newValue) => setSelectedMatchup(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: '32px' }}
              >
                <Tab label="All" value="All" sx={{ minHeight: '32px', padding: '6px 12px', fontSize: isMobile ? '12px' : '14px' }} />
                {matchups.map(matchup => (
  <Tab
    key={matchup.eventShortName}
    value={matchup.eventShortName}
    sx={{ minHeight: '32px', padding: '6px 12px', fontSize: isMobile ? '12px' : '14px' }}
    label={
      matchup.isPGA ? (
        // PGA: Show event name as text
        <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>
          {matchup.eventName || matchup.eventShortName}
        </Typography>
      ) : (
        // Other sports: Show away @ home with logos
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {matchup.awayLogo ? (
            <img
              src={matchup.awayLogo}
              alt="Away Team Logo"
              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
            />
          ) : (
            <Typography sx={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 600 }}>
              {matchup.awayShort}
            </Typography>
          )}
          <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>@</Typography>
          {matchup.homeLogo ? (
            <img
              src={matchup.homeLogo}
              alt="Home Team Logo"
              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
            />
          ) : (
            <Typography sx={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 600 }}>
              {matchup.homeShort}
            </Typography>
          )}
        </Box>
      )
    }
  />
))}
              </Tabs>
            </Box>
          </Box>
          <Box
            className="table-section"
            sx={{
              height: isMobile ? 'calc(100vh - 180px) !important' : 'calc(100vh - 147px) !important',
              overflowY: 'auto',
            }}
          >
            <Box sx={{ width: '100%', height: '100%' }}>
              {tableData.rows.length === 0 ? (
                <Box className="empty-state" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1">
                    No prop bets available for the selected filters.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: '100%', width: '100%' }}>
                  <DataGridPro
                    apiRef={apiRef}
                    rows={tableData.rows}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[10, 25, 50]}
                    pagination
                    page={page}
                    onPageChange={(newPage) => setPage(newPage)}
                    disableSelectionOnClick
                    disableRowSelectionOnClick
                    disableColumnMenu
                    disableColumnSelector
                    disableColumnFilter
                    rowHeight={70}
                    onColumnOrderChange={handleColumnOrderChange}
                    sx={{
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#1e1e1e',
                        borderBottom: '2px solid #444',
                        position: 'sticky',
                        top: '0',
                        zIndex: 2,
                      },
                      '& .MuiDataGrid-columnHeader': {
                        userSelect: 'none',
                        cursor: 'move',
                        outline: 'none !important',
                        '&:focus': {
                          outline: 'none !important',
                        },
                        '&:focus-within': {
                          outline: 'none !important',
                        },
                      },
                      '& .MuiDataGrid-columnHeader--alignRight': {
                        justifyContent: 'flex-end !important',
                      },
                      '& .MuiDataGrid-columnHeaderTitleContainer': {
                        outline: 'none !important',
                        '&:focus': {
                          outline: 'none !important',
                        },
                      },
                      '& .MuiDataGrid-cell': {
                        color: '#ffffff !important',
                        userSelect: 'none',
                        padding: 0,
                        borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                      },
                      '& .MuiDataGrid-cell:last-child': {
                        borderRight: 'none',
                      },
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
                      '& .MuiDataGrid-columnSeparator': { 
                        visibility: 'visible',
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: 'auto !important',
                      },
                      '& .MuiDataGrid-root': {
                        overflowY: 'auto !important',
                        margin: 0,
                      },
                      '& .MuiDataGrid-main': {
                        padding: 0,
                      },
                      '& .MuiDataGrid-footerContainer': {
                        backgroundColor: '#1e1e1e',
                        color: '#ffffff !important',
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1,
                      },
                      '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        color: '#ffffff !important',
                      },
                      '& .MuiSelect-icon': { color: '#ffffff !important' },
                      '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                      '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#ffffff !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.2,
                      },
                      '& .MuiDataGrid-columnHeaderDraggableContainer': {
                        cursor: 'move',
                      },
                    }}
                    rowBuffer={5}
                    rowThreshold={2}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        
        {/* Game Log Modal */}
        <GameLogModal
          open={gameLogModalOpen}
          onClose={handleCloseModal}
          playerData={selectedPlayerData}
          gameLogData={gameLogData}
          loading={gameLogLoading}
          sport={gameLogSportRef.current}
        />
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Props;